// Shared helpers for snapshot date handling

/**
 * Extracts the effective date from a snapshot object
 * Supports Firestore Timestamps, native Date objects, and parsable string values
 * 
 * @param {Object} snap - Snapshot object containing date information
 * @param {Object} [snap.snapshotDate] - Primary snapshot date (Firestore Timestamp or Date)
 * @param {Object} [snap.createdAt] - Fallback creation date (Firestore Timestamp or Date)
 * @returns {Date|null} - Valid Date object or null if parsing fails
 */
export const getEffectiveSnapshotDate = (snap) => {
    if (!snap) return null;
    const src = snap.snapshotDate || snap.createdAt;
    if (!src) return null;

    // Firestore Timestamp
    if (typeof src.toDate === 'function') {
        const d = src.toDate();
        return isNaN(d?.getTime?.() ?? NaN) ? null : d;
    }

    // Native Date
    if (src instanceof Date) {
        return isNaN(src.getTime()) ? null : src;
    }

    // Fallback: attempt to parse other value types
    const parsed = new Date(src);
    return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Converts snapshot date to milliseconds since epoch
 * 
 * @param {Object} snap - Snapshot object
 * @returns {number|null} - Timestamp in milliseconds or null if invalid
 */
export const getEffectiveSnapshotMillis = (snap) => {
    const d = getEffectiveSnapshotDate(snap);
    return d ? d.getTime() : null;
};

/**
 * Parses a date input string (YYYY-MM-DD format) into a local Date object
 * Commonly used for HTML <input type="date"> values
 * 
 * @param {string} value - Date string in YYYY-MM-DD format
 * @returns {Date|null} - Local Date object or null if parsing fails
 */
export const parseDateInputToLocalDate = (value) => {
    if (!value || typeof value !== 'string') return null;
    const parts = value.split('-');
    if (parts.length !== 3) return null;

    const [yearStr, monthStr, dayStr] = parts;
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);

    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
        return null;
    }

    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
};

